import * as React from 'react';
import { MigrationRun } from 'src/types/TMigration';
import { Note, Pill, Stat, formatBytes, timeAgo } from './parts';
import styles from './styles.module.scss';

/**
 * The counters for one library copy.
 *
 * Rendered both for a copy in flight and for a finished one; the only
 * difference is the pill and the sentence above it. Its own component because
 * it is shown from two places — inside the copy panel while the customer is
 * watching, and on the section itself once they have closed the panel.
 */

export const isTerminal = (run?: MigrationRun | null) =>
  !run || ['done', 'failed', 'cancelled', 'interrupted'].includes(run.status);

export const RunReport: React.FC<{ run: MigrationRun }> = ({ run }) => {
  const s = run.stats;
  const running = !isTerminal(run);
  const unusable = (s?.broken || 0) + (s?.known_broken || 0);

  return (
    <div className={styles.runReport}>
      <div className={styles.runHeader}>
        <span>
          <strong>{run.dry_run ? 'Preview' : 'Copy'}</strong>
          <span className={styles.inlineMeta}>
            {' '}
            · started {timeAgo(run.created_at)}
            {s?.source ? ` · reading ${s.source}` : ''}
          </span>
        </span>
        <Pill
          tone={running ? 'busy' : run.status === 'done' ? 'good' : run.status === 'failed' ? 'bad' : 'warn'}
        >
          {running ? 'in progress' : run.status}
        </Pill>
      </div>

      {s && (
        <div className={styles.statRow}>
          <Stat value={s.listed} label="Found" hint="Images your provider reported." />
          {/* On a preview, `copied` is what WOULD be copied — the runner counts
              it and stops before fetching anything. Showing a dash here hid the
              one number a preview exists to produce. */}
          <Stat
            tone="good"
            value={s.copied}
            label={run.dry_run ? 'Would copy' : 'Copied'}
            hint={run.dry_run ? 'Nothing was created — this is what a real copy would bring across.' : undefined}
          />
          <Stat value={s.skipped} label="Already here" hint="Nothing was fetched for these." />
          <Stat
            tone={s.excluded ? 'warn' : 'neutral'}
            value={s.excluded}
            label="Skipped"
            hint="Your provider said we should not copy these — see below."
          />
          <Stat tone={s.failed ? 'bad' : 'neutral'} value={s.failed} label="Failed" />
          <Stat
            tone={unusable ? 'bad' : 'neutral'}
            value={unusable}
            label="Cannot be used"
            hint="Copied, but we cannot process the file."
          />
        </div>
      )}

      {s && s.bytes_expected > 0 && (
        <p className={styles.rowHelp}>
          {formatBytes(s.bytes_expected)} of original files
          {s.still_processing ? ` · ${s.still_processing} still processing` : ''}
        </p>
      )}

      {/* Deliberately does not name a single cause. A run's stats carry the
          paths but not the reasons, and the two real causes — a download that
          failed at the provider, and a file we cannot decode — need opposite
          actions. The section behind this dialog reports the breakdown. */}
      {unusable > 0 && (
        <Note tone="warn">
          <strong>{unusable} images came across but cannot be used.</strong> Either we could not download
          them from your provider, or we could not read the image file. The breakdown, and a download of
          every affected file, are on the page behind this window.
        </Note>
      )}

      {s?.excluded_reasons && Object.keys(s.excluded_reasons).length > 0 && (
        <table className={styles.table}>
          <tbody>
            {Object.entries(s.excluded_reasons).map(([reason, count]) => (
              <tr key={reason}>
                <td>{reason}</td>
                <td className={styles.tableNum}>{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {run.status === 'interrupted' && (
        <Note tone="warn">
          {run.error || 'This stopped before it finished.'} Starting it again picks up where it left off —
          nothing already copied gets copied twice.
        </Note>
      )}
      {run.status === 'failed' && run.error && <Note tone="warn">{run.error}</Note>}
    </div>
  );
};

export default RunReport;
